import { colors } from "@styles/colors";
import { View, XStack, YStack, Text, Button } from "tamagui";
import { RiHome4Line, RiTimeLine, RiFileCopyLine, RiCheckLine } from "react-icons/ri";
import { Coupon } from "./couponData";
import { useState } from "react";
import { appToast } from "@components/Toast";
import { formatDate } from "@utils/func/timeFormatter";
import { isBeforeUseStart, couponExpiryLabel } from "@utils/func/couponPeriod";

interface CouponCardProps {
  coupon: Coupon;
  accentColor: string;
  secondaryColor: string;
  accLabel?: string;
  accColor?: string;
  /** 給定時整張券可點（例：點券去下單）。卡內複製鈕會阻止冒泡，不觸發此事件。 */
  onPress?: () => void;
}

export const CouponCard = ({ coupon, accentColor, secondaryColor, accLabel, accColor, onPress }: CouponCardProps) => {
  const isUsed = coupon.used || !!coupon.usedBy;
  const usedByMe = coupon.usedBy === "我";
  const notYetUsable = !isUsed && isBeforeUseStart(coupon.useStart);
  const [copied, setCopied] = useState(false);

  const handleCopy = (e?: { stopPropagation?: () => void }) => {
    e?.stopPropagation?.(); // 避免觸發整張券的 onPress（點券去下單）
    if (!coupon.code) return;
    navigator.clipboard.writeText(coupon.code);
    setCopied(true);
    appToast.success("序號已複製");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <XStack
      bg={colors.light}
      br="$4"
      ov="hidden"
      pos="relative"
      opacity={isUsed ? 0.55 : 1}
      elevation={2}
      cursor={onPress ? "pointer" : undefined}
      pressStyle={onPress ? { opacity: 0.92 } : undefined}
      onPress={onPress}>

      {/* Used stamp */}
      {isUsed && (
        <View pos="absolute" t={12} r={12} px="$2" py="$1" bw={2} bc={colors.gray} br={6} rotate="8deg" bg="rgba(255,255,255,0.9)" zi={2}>
          <Text color={colors.gray} fow="800" fontSize={11} ls={1}>
            已使用
          </Text>
        </View>
      )}

      {/* Left amount panel */}
      <YStack
        bg={secondaryColor}
        p="$4"
        ai="center"
        jc="center"
        flexShrink={0}
        miw={108}
        style={{ background: `linear-gradient(135deg, ${secondaryColor}, ${accentColor}30)` }}>
        {/* 「折抵」只是標籤，金額才是這張券的主秀：標籤降到 10px／不加粗、透明度壓下來，
            數字提到 38px 並收緊行高與字距，兩者的層級才拉得開。 */}
        <Text fontSize={10} color={accentColor} fow="600" opacity={0.75} mb={1}>
          折抵
        </Text>
        <XStack ai="baseline" jc="center" gap={1}>
          <Text fontSize={15} fow="800" color={accentColor}>
            $
          </Text>
          <Text
            fontSize={38}
            fow="900"
            color={accentColor}
            ls={-1.5}
            lineHeight={38}
            style={{ fontVariantNumeric: "tabular-nums" }}>
            {coupon.amount}
          </Text>
        </XStack>
      </YStack>

      {/* Perforation */}
      <View
        w={2}
        style={{
          background: `radial-gradient(circle, ${colors.gray100} 1.5px, transparent 1.5px)`,
          backgroundSize: "2px 8px",
          backgroundRepeat: "repeat-y",
        }}
      />

      {/* Right details */}
      <YStack f={1} p="$3.5" miw={0} gap="$1.5">
        <Text fontSize={14} fow="700" color={colors.grayDark} pr={isUsed ? 60 : 0}>
          {coupon.title}
        </Text>

        <YStack gap="$1">
          {accLabel && (
            <XStack ai="center" gap="$1.5">
              <View w={6} h={6} br={3} bg={accColor} flexShrink={0} />
              <Text fontSize={11} color={accColor} fow="700">
                {accLabel}
              </Text>
            </XStack>
          )}

          <XStack ai="center" gap="$1.5">
            <RiHome4Line size={12} color={colors.gray} />
            <Text fontSize={11} color={colors.gray} numberOfLines={1}>
              {coupon.scope}
            </Text>
          </XStack>

          {notYetUsable && coupon.useStart && (
            <XStack ai="center" gap="$1.5">
              <RiTimeLine size={12} color={colors.gray} />
              <Text fontSize={11} color={colors.gray} fow="700">
                {formatDate(coupon.useStart)} 起可用
              </Text>
            </XStack>
          )}

          <XStack ai="center" gap="$1.5">
            <RiTimeLine size={12} color={colors.amber} />
            <Text fontSize={11} color={isUsed ? colors.gray : colors.amberDark} fow="600">
              {couponExpiryLabel(coupon.expiry)}
            </Text>
          </XStack>

          {coupon.code && !isUsed && (
            <XStack mt="$1" bg="$gray2" br="$2" px="$2" ai="center" jc="space-between" bw={1} bc="$gray4" style={{ borderStyle: "dashed" }}>
              <Text fontSize={12} fow="700" color={colors.primary} ls={1}>
                {coupon.code}
              </Text>
              <Button
                size="$1.5"
                p={0}
                w={28}
                h={28}
                bg="transparent"
                onPress={(e: any) => handleCopy(e)}
                icon={copied ? <RiCheckLine size={14} color={colors.green} /> : <RiFileCopyLine size={14} color={colors.primary} />}
              />
            </XStack>
          )}

          {coupon.usedBy && (
            <XStack mt="$1" p="$2" bg={usedByMe ? `${accColor}12` : colors.bg} br={6} ai="center" gap="$1.5">
              <View w={16} h={16} br={8} bg={`${accColor}30`} ai="center" jc="center" flexShrink={0}>
                <Text fontSize={9} fow="700" color={accColor}>
                  {coupon.usedBy.charAt(0)}
                </Text>
              </View>
              <Text fontSize={10} color={colors.gray}>
                <Text fow="700" color={colors.grayDark}>
                  {coupon.usedBy}
                </Text>{" "}
                於 {coupon.usedDate} 使用
              </Text>
            </XStack>
          )}
        </YStack>
      </YStack>
    </XStack>
  );
};
